<template>
  <div class="pixso-page automotive-product" :class="pageClasses">
    <main class="automotive-product__main">
      <section class="automotive-product__hero" :aria-labelledby="heroTitleId">
        <div class="automotive-product__hero-stage">
          <div class="automotive-product__hero-composition">
            <h1
              :id="heroTitleId"
              class="automotive-product__hero-title"
              :class="{ 'automotive-product__hero-title--long': isLongHeroTitle }"
            >
              {{ product.title }}
            </h1>
            <img
              class="automotive-product__hero-image"
              :src="product.images.hero"
              :alt="t('ProductPage.productImageAlt', { title: product.title })"
            />
          </div>
        </div>
      </section>

      <section class="automotive-product__intro pixso-stage">
        <div class="automotive-product__intro-row">
          <figure class="product-figure product-figure--description automotive-product__figure">
            <div ref="descriptionVisualRef" class="product-figure__visual">
              <img class="product-figure__image" :src="product.images.description" :alt="product.title" />
              <span
                v-for="(tag, index) in product.descriptionTags"
                :key="tag.key"
                class="product-figure__tag"
                :class="[
                  `product-figure__tag--${index}`,
                  { 'product-figure__tag--revealed': descriptionTagsVisible }
                ]"
                :style="{ '--product-tag-reveal-delay': `${index * 90}ms` }"
              >
                <span
                  v-if="tag.image"
                  class="product-figure__tag-icon product-figure__tag-icon--image"
                  aria-hidden="true"
                >
                  <img :src="tag.image" alt="" />
                </span>
                <span
                  v-else
                  class="product-figure__tag-icon"
                  :class="`product-figure__tag-icon--${tag.icon}`"
                  aria-hidden="true"
                ></span>
                <span class="product-figure__tag-text">{{ tag.label }}</span>
              </span>
            </div>
            <figcaption class="product-figure__caption">{{ product.figureCaption || product.title }}</figcaption>
          </figure>
          <div class="automotive-product__copy">
            <h2>{{ t('ProductPage.descriptionTitle') }}</h2>
            <p
              ref="descriptionCopyRef"
              class="automotive-product__body-text"
              :class="{ 'automotive-product__body-text--revealed': descriptionCopyVisible }"
              data-text-reveal="off"
              v-html="formattedDescription"
            ></p>
          </div>
        </div>

        <div class="automotive-product__intro-row automotive-product__intro-row--reverse">
          <div class="automotive-product__copy">
            <h2>{{ t('ProductPage.featuresTitle') }}</h2>
            <p
              ref="featureCopyRef"
              class="automotive-product__body-text"
              :class="{ 'automotive-product__body-text--revealed': featureCopyVisible }"
              data-text-reveal="off"
              v-html="formattedFeatureDescription"
            ></p>
          </div>
          <figure class="product-figure product-figure--feature automotive-product__figure">
            <div ref="featureVisualRef" class="product-figure__visual">
              <img class="product-figure__image" :src="product.images.feature" :alt="product.title" />
              <span
                v-for="(tag, index) in product.featureTags"
                :key="tag.key"
                class="product-figure__tag"
                :class="[
                  `product-figure__tag--${index}`,
                  { 'product-figure__tag--revealed': featureTagsVisible }
                ]"
                :style="{ '--product-tag-reveal-delay': `${index * 90}ms` }"
              >
                <span
                  v-if="tag.image"
                  class="product-figure__tag-icon product-figure__tag-icon--image"
                  aria-hidden="true"
                >
                  <img :src="tag.image" alt="" />
                </span>
                <span
                  v-else
                  class="product-figure__tag-icon"
                  :class="`product-figure__tag-icon--${tag.icon}`"
                  aria-hidden="true"
                ></span>
                <span class="product-figure__tag-text">{{ tag.label }}</span>
              </span>
            </div>
            <figcaption class="product-figure__caption">{{ product.figureCaption || product.title }}</figcaption>
          </figure>
        </div>
      </section>

      <section class="automotive-product__scenarios pixso-stage" aria-labelledby="scenarioTitleId">
        <h2 :id="scenarioTitleId">{{ t('ProductPage.scenariosTitle') }}</h2>
        <ScenarioGrid
          v-if="product.scenarios?.length"
          class="automotive-product__scenario-grid"
          :items="formattedScenarios"
          :aria-label="t('ProductPage.scenariosTitle')"
          :desktop-columns="3"
          :tablet-columns="2"
          :mobile-columns="1"
          gap="24px"
          card-radius="var(--pixso-radius-lg)"
          card-aspect-ratio="584 / 382"
          title-font-size="var(--pixso-fs-36)"
          title-line-height="var(--pixso-lh-36)"
          copy-inset-x="36px"
          copy-inset-bottom="24px"
        />
      </section>

      <section class="automotive-product__specs pixso-stage" aria-labelledby="specTitleId">
        <h2 :id="specTitleId">{{ t('ProductPage.specsTitle') }}</h2>
        <div class="automotive-product__spec-table" :aria-label="t('ProductPage.productSpecsAria', { title: product.title })">
          <SpecTable :specData="specTableData" />
        </div>

        <router-link class="automotive-product__download" to="/about/contact#contact-form">
          {{ t('ProductPage.downloadButton') }}
        </router-link>
      </section>
    </main>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import SpecTable from '@/components/SpecTable.vue'
import ScenarioGrid from '@/components/pixso/ScenarioGrid.vue'
import { formatSmartTextHtml } from '@/utils/formatSmartTextHtml'

const { t } = useI18n()

const props = defineProps({
  product: {
    type: Object,
    required: true
  }
})

const heroTitleId = computed(() => `product-hero-${props.product.slug}`)
const scenarioTitleId = computed(() => `product-scenario-${props.product.slug}`)
const specTitleId = computed(() => `product-spec-${props.product.slug}`)
const isIndustryDetail = computed(() => props.product.variant === 'industry-detail')
const pageClasses = computed(() => ({
  'automotive-product--industry-detail': isIndustryDetail.value,
  [`automotive-product--${props.product.slug}`]: Boolean(props.product.slug)
}))
const descriptionVisualRef = ref(null)
const featureVisualRef = ref(null)
const descriptionCopyRef = ref(null)
const featureCopyRef = ref(null)
const descriptionTagsVisible = ref(false)
const featureTagsVisible = ref(false)
const descriptionCopyVisible = ref(false)
const featureCopyVisible = ref(false)
let tagRevealObserver = null

const revealAllTags = () => {
  descriptionTagsVisible.value = true
  featureTagsVisible.value = true
  descriptionCopyVisible.value = true
  featureCopyVisible.value = true
}

const isLongHeroTitle = computed(() => {
  if (props.product.heroTitleSize === 'large') return false
  if (props.product.heroTitleSize === 'compact') return true

  return props.product.title.length > 6
})

const bodyNoWrapPhrases = [
  '单天线高精度组合导航系统',
  '多源数据深度融合解算',
  '复杂遮挡/干扰场景',
  '复杂环境下',
  '厘米级连续可靠定位',
  '自研组合导航算法',
  '厘米级定位结果',
  '全球卫星导航系统',
  '车辆底盘动态数据',
  '车辆实时速度',
  '三维位姿信息',
  '轮速、挡位',
  'GNSS 信号',
  'CAN/CAN FD',
  'ASIL-B',
  'GNSS',
  'IMU',
  'RTK'
]
const bodyTextCharDelayMs = 12

const escapeHtml = (value) =>
  String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const formatBodyTextHtml = (text) => {
  const source = String(text || '')
  const phrases = bodyNoWrapPhrases
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)

  if (!source || phrases.length === 0) {
    return escapeHtml(source)
  }

  const pattern = new RegExp(phrases.map(escapeRegExp).join('|'), 'gu')
  let html = ''
  let lastIndex = 0
  let charIndex = 0
  const renderCharacters = (value) =>
    Array.from(value)
      .map((char) => {
        if (char === '\n') {
          return '<br>'
        }

        const delay = charIndex * bodyTextCharDelayMs
        charIndex += 1
        const charHtml = escapeHtml(char)

        return `<span class="automotive-product__text-char" style="--product-body-char-delay: ${delay}ms">${charHtml}</span>`
      })
      .join('')

  source.replace(pattern, (match, offset) => {
    html += renderCharacters(source.slice(lastIndex, offset))
    html += `<span class="automotive-product__text-nowrap">${renderCharacters(match)}</span>`
    lastIndex = offset + match.length
    return match
  })

  html += renderCharacters(source.slice(lastIndex))
  return html
}

const formattedDescription = computed(() => formatBodyTextHtml(props.product.description))
const formattedFeatureDescription = computed(() => formatBodyTextHtml(props.product.featureDescription))

const formattedScenarios = computed(() =>
  (props.product.scenarios || []).map((scenario) => ({
    ...scenario,
    descriptionHtml: formatSmartTextHtml(scenario.description, ['???????'])
  }))
)

const specTableData = computed(() => {
  return props.product.specTabs.map((tab) => ({
    key: tab.label || tab.key,
    tableData: Array.isArray(tab.tableData)
      ? tab.tableData
      : tab.rows.map((row) => ({
          title: row.label,
          contentList: Array.isArray(row.value) ? row.value : [row.value]
        }))
  }))
})

onMounted(() => {
  if (typeof window === 'undefined') {
    return
  }

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
    revealAllTags()
    return
  }

  tagRevealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return

        if (entry.target === descriptionVisualRef.value) {
          descriptionTagsVisible.value = true
        }

        if (entry.target === featureVisualRef.value) {
          featureTagsVisible.value = true
        }

        if (entry.target === descriptionCopyRef.value) {
          descriptionCopyVisible.value = true
        }

        if (entry.target === featureCopyRef.value) {
          featureCopyVisible.value = true
        }

        tagRevealObserver?.unobserve(entry.target)
      })
    },
    {
      threshold: 0.35,
      rootMargin: '0px 0px -8% 0px'
    }
  )

  if (descriptionVisualRef.value) {
    tagRevealObserver.observe(descriptionVisualRef.value)
  }

  if (featureVisualRef.value) {
    tagRevealObserver.observe(featureVisualRef.value)
  }

  if (descriptionCopyRef.value) {
    tagRevealObserver.observe(descriptionCopyRef.value)
  }

  if (featureCopyRef.value) {
    tagRevealObserver.observe(featureCopyRef.value)
  }
})

onBeforeUnmount(() => {
  tagRevealObserver?.disconnect()
  tagRevealObserver = null
})
</script>

<style scoped>
.automotive-product {
  --product-detail-hero-title-max: 450px;
  --product-detail-hero-title-long-max: 380px;
  --product-detail-hero-image-overlap: clamp(58px, calc(-4.0769px + 16.4103vw), 311px);
  --product-detail-hero-image-bottom-trim: clamp(35px, 9.8vw, 188px);
  --product-detail-hero-image-width: clamp(260px, 100vw, var(--pixso-page-max));
  --product-detail-hero-bg-end: #3a3636;
  --product-detail-hero-bg:
    radial-gradient(circle at 50% 42%, rgba(255, 255, 255, 0.08) 0, rgba(255, 255, 255, 0) 34%),
    linear-gradient(180deg, var(--pixso-color-bg) 0%, var(--product-detail-hero-bg-end) 100%);
  --product-detail-intro-padding-top: var(--pixso-fs-96);
  --product-detail-intro-padding-bottom: var(--pixso-section-gap-xl);
  --product-detail-intro-row-max: 1152px;
  --product-detail-intro-row-height: 600px;
  --product-detail-intro-copy-padding-y: 10px;
  --product-detail-figure-caption-bottom: var(--pixso-fs-96);
  --product-detail-tag-width: 144px;
  --product-detail-tag-height: 81px;
  --product-detail-tag-padding-x: 8px;
  --product-detail-tag-radius: 8px;
  --product-detail-tag-icon-ratio: 38%;
  --product-detail-tag-icon-max: 36px;
  --product-detail-tag-text-max: 128px;
  --product-detail-tag-bg: rgba(255, 255, 255, 0.2);
  --product-detail-scenario-max: var(--pixso-page-max);
  --product-detail-scenario-top: 52px;
  --product-detail-industry-scenario-max: 1792px;
  --product-detail-industry-scenario-bottom: 110px;
  --product-detail-industry-spec-padding-top: 250px;
  --product-detail-industry-spec-padding-bottom: 260px;
  --product-detail-industry-spec-table-top: var(--pixso-gap-lg);
  --product-detail-industry-spec-content-max: 1152px;
  --product-detail-industry-spec-tabs-max: var(--product-detail-industry-spec-content-max);
  --product-detail-industry-spec-tabs-bottom: 121px;
  --product-detail-industry-spec-tabs-row-gap: var(--pixso-gap-md);
  --product-detail-industry-spec-button-min: 0px;
  --product-detail-industry-spec-button-height: 50px;
  --product-detail-industry-spec-button-padding-x: 60px;
  --product-detail-industry-table-line-padding-y: 40px;
  --product-detail-industry-table-title-width: 24.21875%;
  --product-detail-industry-table-content-width: 75.78125%;
  --product-detail-industry-download-width: 280px;
  --product-detail-industry-download-height: 50px;
  --product-detail-spec-max: 1200px;
  --product-detail-spec-padding-top: var(--pixso-section-gap-xl);
  --product-detail-spec-padding-bottom: 260px;
  --product-detail-spec-table-top: 54px;
  --product-detail-spec-tabs-max: 900px;
  --product-detail-spec-tabs-bottom: 106px;
  --product-detail-spec-tabs-row-gap: 18px;
  --product-detail-spec-button-min: 180px;
  --product-detail-spec-button-wide-min: 300px;
  --product-detail-spec-button-height: 40px;
  --product-detail-spec-button-padding-x: var(--pixso-fs-32);
  --product-detail-spec-border: rgba(255, 255, 255, 0.86);
  --product-detail-table-divider: rgba(217, 217, 217, 0.32);
  --product-detail-inverse-text: var(--pixso-color-bg);
  --product-detail-button-width: 200px;
  --product-detail-button-height: 42px;
  --product-detail-button-radius: 999px;
  --product-detail-tablet-intro-padding-y: 180px;
  --product-detail-tablet-intro-padding-top: 120px;
  --product-detail-tablet-row-max: 960px;
  --product-detail-tablet-row-height: 500px;
  --product-detail-tablet-tag-width: 128px;
  --product-detail-tablet-tag-height: 72px;
  --product-detail-tablet-tag-padding-y: 8px;
  --product-detail-tablet-tag-padding-x: 8px;
  --product-detail-tablet-tag-text-max: 112px;
  --product-detail-tablet-icon-size: 40px;
  --product-detail-tablet-spec-max: 1120px;
  --product-detail-mobile-intro-padding-top: var(--pixso-fs-72);
  --product-detail-mobile-intro-padding-bottom: 120px;
  --product-detail-mobile-row-gap: var(--pixso-fs-36);
  --product-detail-mobile-row-stack-gap: var(--pixso-fs-96);
  --product-detail-mobile-tag-width: 104px;
  --product-detail-mobile-tag-height: 64px;
  --product-detail-mobile-tag-padding-y: 8px;
  --product-detail-mobile-tag-padding-x: 6px;
  --product-detail-mobile-icon-size: 32px;
  --product-detail-mobile-spec-table-top: 46px;
  --product-detail-mobile-spec-tabs-bottom: 56px;
  --product-detail-mobile-spec-button-min: 220px;
  --product-detail-mobile-button-height: 36px;
  --product-detail-mobile-button-padding-x: 18px;
}

.automotive-product__main {
  width: 100%;
  background: var(--pixso-color-bg);
}

.automotive-product__hero {
  width: 100%;
  height: var(--pixso-hero-height);
  min-height: var(--pixso-hero-min);
  overflow: hidden;
  background: var(--product-detail-hero-bg);
}

.automotive-product__hero-stage {
  width: min(100%, var(--pixso-page-max));
  height: 100%;
  margin: 0 auto;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.automotive-product__hero-composition {
  position: relative;
  width: var(--product-detail-hero-image-width);
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
}

.automotive-product__hero-title {
  position: relative;
  z-index: 1;
  margin: 0;
  color: var(--pixso-color-text);
  font-family: var(--pixso-font-en);
  font-size: var(--pixso-fs-product-hero);
  font-weight: 500;
  line-height: var(--pixso-lh-product-hero);
  letter-spacing: 0;
  white-space: nowrap;
}

.automotive-product__hero-title--long {
  font-size: var(--pixso-fs-product-hero-long);
  line-height: var(--pixso-lh-product-hero-long);
}

.automotive-product__hero-image {
  position: relative;
  display: block;
  z-index: 2;
  width: 100%;
  height: auto;
  margin-top: calc(var(--product-detail-hero-image-overlap) * -1);
  margin-bottom: calc(var(--product-detail-hero-image-bottom-trim) * -1);
  object-position: center;
}

.automotive-product__intro {
  padding: var(--product-detail-intro-padding-top) 0 var(--product-detail-intro-padding-bottom);
}

.automotive-product__intro-row {
  width: min(calc(100% - var(--pixso-gap-lg)), var(--product-detail-intro-row-max));
  min-height: var(--product-detail-intro-row-height);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: stretch;
  gap: var(--pixso-gap-md);
}

.automotive-product__intro-row + .automotive-product__intro-row {
  margin-top: 0;
}

.automotive-product__intro-row--reverse {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.automotive-product__copy {
  min-height: var(--product-detail-intro-row-height);
  padding: var(--product-detail-intro-copy-padding-y) var(--pixso-gap-lg);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
}

.automotive-product__copy h2,
.automotive-product__scenarios h2,
.automotive-product__specs h2 {
  margin: 0;
  color: var(--pixso-color-text);
  font-weight: 700;
  letter-spacing: 0;
}

.automotive-product__copy h2 {
  font-size: var(--pixso-fs-64);
  line-height: var(--pixso-lh-64);
}

.automotive-product__copy p {
  max-width: none;
  margin: var(--pixso-fs-16) 0 0;
  color: var(--pixso-color-muted);
  font-size: var(--pixso-fs-20);
  font-weight: 700;
  line-height: var(--pixso-lh-20);
  overflow-wrap: break-word;
  word-break: normal;
  line-break: loose;
  text-wrap: pretty;
}

@supports (word-break: auto-phrase) {
  .automotive-product__body-text {
    word-break: auto-phrase;
  }
}

.automotive-product__copy p :deep(.automotive-product__nowrap) {
  display: inline-block;
  white-space: nowrap;
}

.automotive-product__body-text :deep(.automotive-product__text-nowrap) {
  white-space: nowrap;
}

.automotive-product__body-text {
  transform: translate3d(0, 18px, 0);
  transition:
    transform 560ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

.automotive-product__body-text :deep(.automotive-product__text-char) {
  opacity: 0;
  filter: blur(8px);
  transition:
    opacity 420ms ease,
    filter 420ms cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: var(--product-body-char-delay, 0ms);
  will-change: opacity, filter;
}

.automotive-product__body-text--revealed {
  transform: translate3d(0, 0, 0);
}

.automotive-product__body-text--revealed :deep(.automotive-product__text-char) {
  opacity: 1;
  filter: blur(0);
}

.product-figure {
  width: 100%;
  margin: 0;
  position: relative;
}

.product-figure__visual {
  width: min(100%, 564px);
  aspect-ratio: 564 / 600;
  height: auto;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
}

.product-figure__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.automotive-product--cx005 .product-figure--description .product-figure__image {
  object-position: center 60%;
  transform: scale(1.32);
  transform-origin: center center;
}

.product-figure__caption {
  position: absolute;
  z-index: 2;
  left: 0;
  right: 0;
  bottom: 16%;
  margin: 0;
  color: var(--pixso-color-text);
  text-align: center;
  font-family: var(--pixso-font-en);
  font-size: var(--pixso-fs-32);
  font-weight: 700;
  line-height: var(--pixso-lh-32);
}

.product-figure__tag {
  position: absolute;
  z-index: 2;
  width: min(144px, 25.5%);
  aspect-ratio: 144 / 81;
  padding: clamp(6px, 1.7%, 10px) clamp(4px, 1.2%, 8px);
  border-radius: var(--product-detail-tag-radius);
  background: var(--product-detail-tag-bg);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: var(--pixso-color-text);
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  box-sizing: border-box;
  opacity: 0;
  transform: translate3d(0, 28px, 0);
  transition:
    opacity 560ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 560ms cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: var(--product-tag-reveal-delay, 0ms);
  will-change: opacity, transform;
}

.product-figure__tag--revealed {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}

.product-figure__tag-icon {
  width: 48px;
  height: 48px;
  border: 1.5px solid currentColor;
  border-radius: 3px;
  position: relative;
}

.product-figure__tag-icon--image {
  border: 0;
  border-radius: 0;
}

.product-figure__tag-icon--image::before,
.product-figure__tag-icon--image::after {
  display: none;
}

.product-figure__tag-icon--image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.product-figure__tag-icon::before,
.product-figure__tag-icon::after {
  content: '';
  position: absolute;
  background: currentColor;
}

.product-figure__tag-icon::before {
  left: 12px;
  right: 12px;
  top: -7px;
  height: 1.5px;
}

.product-figure__tag-icon::after {
  left: 50%;
  top: 12px;
  width: 1.5px;
  height: 24px;
  transform: translateX(-50%);
}

.product-figure__tag-icon--satellite {
  width: 48px;
  height: 48px;
  border: 0;
  border-radius: 0;
  transform: rotate(-45deg);
}

.product-figure__tag-icon--satellite::before {
  left: 20px;
  right: auto;
  top: 4px;
  width: 8px;
  height: 40px;
  border-radius: 3px;
}

.product-figure__tag-icon--satellite::after {
  left: 4px;
  top: 20px;
  width: 40px;
  height: 8px;
  border-radius: 3px;
  transform: none;
}

.product-figure__tag-icon--rtk {
  width: 48px;
  height: 48px;
  border: 0;
  border-top: 4px solid currentColor;
  border-radius: 50% 50% 0 0;
}

.product-figure__tag-icon--rtk::before {
  left: 22px;
  right: auto;
  top: 8px;
  width: 4px;
  height: 32px;
}

.product-figure__tag-icon--rtk::after {
  left: 8px;
  top: 38px;
  width: 32px;
  height: 4px;
  transform: none;
}

.product-figure__tag-icon--shield {
  width: 48px;
  height: 48px;
  border-radius: 8px 8px 20px 20px;
}

.product-figure__tag-icon--shield::before {
  left: 14px;
  right: auto;
  top: 24px;
  width: 10px;
  height: 3px;
  transform: rotate(45deg);
}

.product-figure__tag-icon--shield::after {
  left: 22px;
  top: 20px;
  width: 18px;
  height: 3px;
  transform: rotate(-45deg);
}

.product-figure__tag-icon--matrix {
  width: 48px;
  height: 48px;
  border: 0;
  border-radius: 0;
  background:
    linear-gradient(currentColor 0 0) 6px 6px / 14px 14px no-repeat,
    linear-gradient(currentColor 0 0) 28px 6px / 14px 14px no-repeat,
    linear-gradient(currentColor 0 0) 6px 28px / 14px 14px no-repeat,
    linear-gradient(currentColor 0 0) 28px 28px / 14px 14px no-repeat;
}

.product-figure__tag-icon--matrix::before,
.product-figure__tag-icon--matrix::after {
  display: none;
}

.product-figure__tag-icon--target {
  width: 48px;
  height: 48px;
  border-radius: 50%;
}

.product-figure__tag-icon--target::before {
  left: 7px;
  right: 7px;
  top: 50%;
  height: 2px;
  transform: translateY(-50%);
}

.product-figure__tag-icon--target::after {
  left: 50%;
  top: 7px;
  width: 2px;
  height: 34px;
}

.product-figure__tag-icon--power {
  width: 48px;
  height: 48px;
  border: 0;
  border-radius: 0;
  transform: rotate(-45deg);
}

.product-figure__tag-icon--power::before {
  left: 8px;
  right: auto;
  top: 22px;
  width: 32px;
  height: 4px;
}

.product-figure__tag-icon--power::after {
  left: 22px;
  top: 8px;
  width: 4px;
  height: 32px;
  transform: none;
}

.product-figure__tag-icon--gyro,
.product-figure__tag-icon--accelerometer,
.product-figure__tag-icon--calibration,
.product-figure__tag-icon--compact,
.product-figure__tag-icon--sync,
.product-figure__tag-icon--environment,
.product-figure__tag-icon--interface,
.product-figure__tag-icon--gnss,
.product-figure__tag-icon--fusion,
.product-figure__tag-icon--attitude,
.product-figure__tag-icon--lowPower {
  width: 48px;
  height: 48px;
  border: 0;
  border-radius: 0;
}

.product-figure__tag-icon--gyro {
  background:
    radial-gradient(circle, transparent 42%, currentColor 44% 49%, transparent 51%),
    linear-gradient(currentColor 0 0) 23px 6px / 2px 36px no-repeat,
    linear-gradient(currentColor 0 0) 6px 23px / 36px 2px no-repeat;
}

.product-figure__tag-icon--accelerometer {
  background:
    linear-gradient(currentColor 0 0) 23px 6px / 2px 36px no-repeat,
    linear-gradient(currentColor 0 0) 6px 23px / 36px 2px no-repeat,
    radial-gradient(circle at 24px 24px, currentColor 0 4px, transparent 5px);
}

.product-figure__tag-icon--calibration {
  border: 2px solid currentColor;
  border-radius: 50%;
}

.product-figure__tag-icon--calibration::before {
  left: 23px;
  top: 7px;
  width: 2px;
  height: 18px;
  transform-origin: bottom center;
  transform: rotate(34deg);
}

.product-figure__tag-icon--calibration::after {
  left: 16px;
  top: 28px;
  width: 16px;
  height: 2px;
  transform: none;
}

.product-figure__tag-icon--compact {
  background:
    linear-gradient(currentColor 0 0) 10px 10px / 10px 2px no-repeat,
    linear-gradient(currentColor 0 0) 10px 10px / 2px 10px no-repeat,
    linear-gradient(currentColor 0 0) 28px 10px / 10px 2px no-repeat,
    linear-gradient(currentColor 0 0) 36px 10px / 2px 10px no-repeat,
    linear-gradient(currentColor 0 0) 10px 36px / 10px 2px no-repeat,
    linear-gradient(currentColor 0 0) 10px 28px / 2px 10px no-repeat,
    linear-gradient(currentColor 0 0) 28px 36px / 10px 2px no-repeat,
    linear-gradient(currentColor 0 0) 36px 28px / 2px 10px no-repeat;
}

.product-figure__tag-icon--sync {
  border: 2px solid currentColor;
  border-left-color: transparent;
  border-radius: 50%;
}

.product-figure__tag-icon--sync::before {
  left: 4px;
  top: 25px;
  width: 9px;
  height: 9px;
  border-left: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  background: transparent;
  transform: rotate(45deg);
}

.product-figure__tag-icon--sync::after {
  display: none;
}

.product-figure__tag-icon--environment {
  border: 2px solid currentColor;
  border-radius: 6px 6px 20px 20px;
}

.product-figure__tag-icon--environment::before {
  left: 14px;
  top: 24px;
  width: 8px;
  height: 2px;
  transform: rotate(45deg);
}

.product-figure__tag-icon--environment::after {
  left: 20px;
  top: 20px;
  width: 16px;
  height: 2px;
  transform: rotate(-45deg);
}

.product-figure__tag-icon--interface {
  border: 2px solid currentColor;
  border-radius: 4px;
}

.product-figure__tag-icon--interface::before {
  left: 12px;
  right: 12px;
  top: -5px;
  height: 2px;
}

.product-figure__tag-icon--interface::after {
  left: 50%;
  top: 11px;
  width: 2px;
  height: 26px;
  transform: translateX(-50%);
}

.product-figure__tag-icon--gnss,
.product-figure__tag-icon--fusion {
  background:
    linear-gradient(currentColor 0 0) 8px 8px / 12px 12px no-repeat,
    linear-gradient(currentColor 0 0) 28px 8px / 12px 12px no-repeat,
    linear-gradient(currentColor 0 0) 8px 28px / 12px 12px no-repeat,
    linear-gradient(currentColor 0 0) 28px 28px / 12px 12px no-repeat;
}

.product-figure__tag-icon--gnss::before,
.product-figure__tag-icon--gnss::after,
.product-figure__tag-icon--fusion::before,
.product-figure__tag-icon--fusion::after {
  display: none;
}

.product-figure__tag-icon--attitude {
  border: 2px solid currentColor;
  border-radius: 50%;
}

.product-figure__tag-icon--attitude::before {
  left: 9px;
  right: 9px;
  top: 50%;
  height: 2px;
  transform: translateY(-50%) rotate(-28deg);
}

.product-figure__tag-icon--attitude::after {
  left: 50%;
  top: 9px;
  width: 2px;
  height: 30px;
  transform: translateX(-50%) rotate(28deg);
}

.product-figure__tag-icon--lowPower {
  transform: rotate(-45deg);
}

.product-figure__tag-icon--lowPower::before {
  left: 7px;
  right: auto;
  top: 22px;
  width: 34px;
  height: 4px;
}

.product-figure__tag-icon--lowPower::after {
  left: 22px;
  top: 7px;
  width: 4px;
  height: 34px;
  transform: none;
}

.product-figure__tag-icon--gyro::before,
.product-figure__tag-icon--gyro::after,
.product-figure__tag-icon--accelerometer::before,
.product-figure__tag-icon--accelerometer::after,
.product-figure__tag-icon--compact::before,
.product-figure__tag-icon--compact::after {
  display: none;
}

.product-figure__tag-text {
  font-size: var(--pixso-fs-10);
  line-height: var(--pixso-lh-10);
  text-align: center;
  max-width: var(--product-detail-tag-text-max);
  margin-top: 4px;
}

.product-figure__tag-icon,
.product-figure__tag-icon--image,
.product-figure__tag-icon--satellite,
.product-figure__tag-icon--rtk,
.product-figure__tag-icon--shield,
.product-figure__tag-icon--matrix,
.product-figure__tag-icon--target,
.product-figure__tag-icon--power,
.product-figure__tag-icon--gyro,
.product-figure__tag-icon--accelerometer,
.product-figure__tag-icon--calibration,
.product-figure__tag-icon--compact,
.product-figure__tag-icon--sync,
.product-figure__tag-icon--environment,
.product-figure__tag-icon--interface,
.product-figure__tag-icon--gnss,
.product-figure__tag-icon--fusion,
.product-figure__tag-icon--attitude,
.product-figure__tag-icon--lowPower {
  width: min(var(--product-detail-tag-icon-max), var(--product-detail-tag-icon-ratio));
  height: auto;
  aspect-ratio: 1;
}

.product-figure--description .product-figure__tag--0 {
  top: 18.5%;
  left: 5%;
}

.product-figure--description .product-figure__tag--1 {
  top: 18.5%;
  right: 5%;
}

.product-figure--description .product-figure__tag--2 {
  top: 34.5%;
  right: 5%;
}

.product-figure--description .product-figure__tag--3 {
  top: 34.5%;
  left: 5%;
}

.product-figure--feature .product-figure__tag--0 {
  top: 20.6667%;
  left: 5%;
}

.product-figure--feature .product-figure__tag--1 {
  top: 36.1667%;
  left: 5%;
}

.product-figure--feature .product-figure__tag--2 {
  top: 36.1667%;
  right: 5%;
}

.product-figure--feature .product-figure__tag--3 {
  top: 51.6667%;
  right: 5%;
}

.product-figure--feature .product-figure__tag--4 {
  top: 51.6667%;
  left: 5%;
}

.automotive-product__scenarios {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--product-detail-scenario-max));
  margin: 0 auto;
}

.automotive-product__scenarios h2 {
  text-align: center;
  font-size: var(--pixso-fs-96);
  line-height: var(--pixso-lh-96);
}

.automotive-product__scenario-grid {
  width: 100%;
  margin-top: var(--product-detail-scenario-top);
}

.automotive-product__specs {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--product-detail-spec-max));
  margin: 0 auto;
  padding: var(--product-detail-spec-padding-top) 0 var(--product-detail-spec-padding-bottom);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.automotive-product__specs h2 {
  text-align: center;
  font-size: var(--pixso-fs-56);
  line-height: var(--pixso-lh-56);
}

.automotive-product__spec-table {
  width: 100%;
  margin-top: var(--product-detail-spec-table-top);
}

.automotive-product__scenario-grid {
  margin-top: var(--product-detail-scenario-top);
}

.automotive-product__spec-table :deep(.spec-table__buttons) {
  width: min(100%, var(--product-detail-spec-tabs-max));
  margin: 0 auto var(--product-detail-spec-tabs-bottom);
  padding-bottom: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--product-detail-spec-tabs-row-gap) var(--pixso-gap-md);
  border-bottom: 0;
}

.automotive-product__spec-table :deep(.spec-table__button) {
  min-width: var(--product-detail-spec-button-min) !important;
  height: var(--product-detail-spec-button-height) !important;
  padding: 0 var(--product-detail-spec-button-padding-x) !important;
  border-color: var(--product-detail-spec-border) !important;
  background: transparent !important;
  color: var(--pixso-color-text) !important;
  font-size: var(--pixso-fs-16) !important;
  font-weight: 700 !important;
  line-height: var(--pixso-lh-16) !important;
}

.automotive-product__spec-table :deep(.spec-table__button:nth-child(5)) {
  min-width: var(--product-detail-spec-button-wide-min) !important;
}

.automotive-product__spec-table :deep(.spec-table__button--active),
.automotive-product__spec-table :deep(.spec-table__button--active:hover) {
  border-color: var(--pixso-color-text) !important;
  background: var(--pixso-color-text) !important;
  color: var(--product-detail-inverse-text) !important;
}

.automotive-product__spec-table :deep(.spec-table__button:hover) {
  border-color: var(--pixso-color-text) !important;
  background: var(--pixso-color-text) !important;
  color: var(--product-detail-inverse-text) !important;
}

.automotive-product__spec-table :deep(.spec-table__content) {
  width: 100%;
}

.automotive-product__spec-table :deep(.table-line) {
  border-bottom-color: var(--product-detail-table-divider);
}

.automotive-product__spec-table :deep(.table-line__container) {
  padding: var(--pixso-fs-32) 0;
  align-items: flex-start;
}

.automotive-product__spec-table :deep(.table-line__title) {
  width: 32%;
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-24);
  line-height: var(--pixso-lh-24);
}

.automotive-product__spec-table :deep(.table-line__content) {
  width: 68%;
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-16);
  line-height: var(--pixso-lh-16);
}

.automotive-product__spec-table :deep(.table-line__list) {
  gap: 0;
}

.automotive-product__spec-table :deep(.table-line__item) {
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-16);
  line-height: var(--pixso-lh-16);
}

.automotive-product__download {
  width: var(--product-detail-button-width);
  height: var(--product-detail-button-height);
  margin-top: var(--pixso-gap-lg);
  border: 0;
  border-radius: var(--product-detail-button-radius);
  background: var(--pixso-color-text);
  color: var(--product-detail-inverse-text);
  font-size: var(--pixso-fs-14);
  font-weight: 700;
  line-height: var(--pixso-lh-14);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.automotive-product--industry-detail .automotive-product__intro {
  padding: var(--product-detail-intro-padding-top) 0 0;
}

.automotive-product--industry-detail .automotive-product__intro-row {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--product-detail-intro-row-max));
}

.automotive-product--industry-detail .automotive-product__copy {
  padding: var(--product-detail-intro-copy-padding-y) var(--pixso-gap-lg);
}

.automotive-product--industry-detail .product-figure__caption {
  bottom: 16%;
}

.automotive-product--industry-detail .product-figure--description .product-figure__tag--0 {
  top: 18.5%;
  right: 5%;
  left: auto;
}

.automotive-product--industry-detail .product-figure--description .product-figure__tag--1 {
  top: 18.5%;
  left: 5%;
  right: auto;
}

.automotive-product--industry-detail .product-figure--description .product-figure__tag--2 {
  top: 34.5%;
  right: 5%;
}

.automotive-product--industry-detail .product-figure--description .product-figure__tag--3 {
  top: 34.5%;
  left: 5%;
}

.automotive-product--industry-detail .product-figure--description .product-figure__tag--4 {
  top: 51.6667%;
  right: 5%;
}

.automotive-product--industry-detail .product-figure--feature .product-figure__tag--0 {
  top: 20.6667%;
  left: 5%;
}

.automotive-product--industry-detail .product-figure--feature .product-figure__tag--1 {
  top: 36.1667%;
  left: 5%;
}

.automotive-product--industry-detail .product-figure--feature .product-figure__tag--2 {
  top: 36.1667%;
  right: 5%;
}

.automotive-product--industry-detail .product-figure--feature .product-figure__tag--3 {
  top: 51.6667%;
  right: 5%;
}

.automotive-product--industry-detail .automotive-product__specs {
  padding: var(--product-detail-industry-spec-padding-top) 0 var(--product-detail-industry-spec-padding-bottom);
}

.automotive-product--industry-detail .automotive-product__specs h2 {
  font-size: var(--pixso-fs-64);
  line-height: var(--pixso-lh-64);
}

.automotive-product--industry-detail .automotive-product__spec-table {
  margin-top: var(--product-detail-industry-spec-table-top);
}

.automotive-product--industry-detail .automotive-product__spec-table :deep(.spec-table__buttons) {
  width: min(100%, var(--product-detail-industry-spec-tabs-max));
  margin-bottom: var(--product-detail-industry-spec-tabs-bottom);
  gap: var(--product-detail-industry-spec-tabs-row-gap) var(--pixso-gap-md);
}

.automotive-product--industry-detail .automotive-product__spec-table :deep(.spec-table__button) {
  min-width: var(--product-detail-industry-spec-button-min) !important;
  height: var(--product-detail-industry-spec-button-height) !important;
  padding: 0 var(--product-detail-industry-spec-button-padding-x) !important;
  font-size: var(--pixso-fs-20) !important;
  line-height: var(--pixso-lh-20) !important;
}

.automotive-product--industry-detail .automotive-product__spec-table :deep(.spec-table__button:nth-child(5)) {
  min-width: var(--product-detail-industry-spec-button-min) !important;
}

.automotive-product--industry-detail .automotive-product__spec-table :deep(.spec-table__content) {
  width: min(100%, var(--product-detail-industry-spec-content-max));
  margin: 0 auto;
}

.automotive-product--industry-detail .automotive-product__spec-table :deep(.table-line__container) {
  padding: var(--product-detail-industry-table-line-padding-y) 0;
}

.automotive-product--industry-detail .automotive-product__spec-table :deep(.table-line__title) {
  width: var(--product-detail-industry-table-title-width);
}

.automotive-product--industry-detail .automotive-product__spec-table :deep(.table-line__content) {
  width: var(--product-detail-industry-table-content-width);
  font-size: var(--pixso-fs-20);
  line-height: var(--pixso-lh-20);
}

.automotive-product--industry-detail .automotive-product__spec-table :deep(.table-line__item) {
  font-size: var(--pixso-fs-20);
  line-height: var(--pixso-lh-20);
}

.automotive-product--industry-detail .automotive-product__download {
  width: var(--product-detail-industry-download-width);
  height: var(--product-detail-industry-download-height);
}

.automotive-product--industry-detail .automotive-product__scenarios {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--product-detail-industry-scenario-max));
  padding: var(--pixso-section-gap-xl) 0 var(--product-detail-industry-scenario-bottom);
}

.automotive-product--industry-detail .automotive-product__scenarios h2 {
  font-size: var(--pixso-fs-96);
  line-height: var(--pixso-lh-96);
}

@media (max-width: 1200px) {
  .automotive-product__intro {
    padding: var(--product-detail-tablet-intro-padding-top) 0 var(--product-detail-tablet-intro-padding-y);
  }

  .automotive-product__intro-row,
  .automotive-product__intro-row--reverse {
    width: min(calc(100% - var(--pixso-page-padding) * 2), var(--product-detail-tablet-row-max));
    min-height: var(--product-detail-tablet-row-height);
    gap: var(--pixso-gap-md);
  }

  .automotive-product__copy {
    min-height: var(--product-detail-tablet-row-height);
  }

  .product-figure__visual {
    height: var(--product-detail-tablet-row-height);
  }

  .product-figure__tag {
    width: var(--product-detail-tablet-tag-width);
    min-height: var(--product-detail-tablet-tag-height);
    padding: var(--product-detail-tablet-tag-padding-y) var(--product-detail-tablet-tag-padding-x);
  }

  .product-figure__tag-text {
    max-width: var(--product-detail-tablet-tag-text-max);
  }

  .product-figure--description .product-figure__tag--0,
  .product-figure--description .product-figure__tag--1 {
    top: 16%;
  }

  .product-figure--description .product-figure__tag--2,
  .product-figure--description .product-figure__tag--3 {
    top: 33%;
  }

  .product-figure--feature .product-figure__tag--0 {
    top: 18%;
  }

  .product-figure--feature .product-figure__tag--1,
  .product-figure--feature .product-figure__tag--2 {
    top: 35%;
  }

  .product-figure--feature .product-figure__tag--3 {
    top: 52%;
  }

  .product-figure--feature .product-figure__tag--4 {
    top: 52%;
  }

  .automotive-product__copy h2,
  .automotive-product__specs h2 {
    font-size: var(--pixso-fs-48);
    line-height: var(--pixso-lh-48);
  }

  .automotive-product__scenarios h2 {
    font-size: var(--pixso-fs-72);
    line-height: var(--pixso-lh-72);
  }

  .automotive-product__specs {
    width: min(calc(100% - var(--pixso-page-padding) * 2), var(--product-detail-tablet-spec-max));
    min-height: 0;
    padding: var(--product-detail-tablet-intro-padding-y) 0;
  }

  .automotive-product--industry-detail .automotive-product__specs {
    min-height: 0;
    padding: var(--product-detail-tablet-intro-padding-y) 0;
  }

  .automotive-product--industry-detail .automotive-product__scenarios {
    padding: var(--product-detail-tablet-intro-padding-y) 0;
  }
}

@media (max-width: 900px) {
  .automotive-product__intro-row,
  .automotive-product__intro-row--reverse {
    grid-template-columns: 1fr;
    min-height: auto;
    gap: var(--product-detail-mobile-row-gap);
  }

  .automotive-product__intro-row--reverse .automotive-product__copy {
    order: 2;
  }

  .automotive-product__intro-row--reverse .automotive-product__figure {
    order: 1;
  }

  .automotive-product__copy {
    width: 100%;
    min-width: 0;
    text-align: center;
    min-height: auto;
    padding: 0;
    align-items: center;
  }

  .automotive-product__copy p {
    max-width: 100%;
    overflow-wrap: break-word;
    word-break: normal;
  }

  .automotive-product__figure {
    min-width: 0;
  }

  .product-figure__visual {
    width: 100%;
    max-width: 564px;
    height: auto;
  }

  .automotive-product__scenario-grid {
    margin-top: var(--product-detail-scenario-top);
  }
}

@media (max-width: 768px) {
  .automotive-product__hero {
    height: var(--pixso-hero-mobile);
    min-height: var(--pixso-hero-mobile);
  }

  .automotive-product__hero-title {
    z-index: 1;
    width: 100%;
    font-size: var(--pixso-fs-96);
    line-height: var(--pixso-lh-96);
    text-align: center;
  }

  .automotive-product__hero-title--long {
    font-size: var(--pixso-fs-72);
    line-height: var(--pixso-lh-72);
  }

  .automotive-product__hero-image {
    z-index: 2;
    max-width: none;
    object-position: center top;
  }

  .automotive-product--p21s {
    --product-detail-hero-image-overlap: clamp(16px, 5vw, 36px);
    --product-detail-hero-image-width: min(132vw, 560px);
  }

  .automotive-product__intro {
    padding: var(--product-detail-mobile-intro-padding-top) 0 var(--product-detail-mobile-intro-padding-bottom);
  }

  .automotive-product__intro-row + .automotive-product__intro-row {
    margin-top: var(--product-detail-mobile-row-stack-gap);
  }

  .automotive-product__copy h2,
  .automotive-product__specs h2 {
    font-size: var(--pixso-fs-36);
    line-height: var(--pixso-lh-36);
  }

  .automotive-product__copy p {
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }

  .automotive-product__scenarios h2 {
    font-size: var(--pixso-fs-48);
    line-height: var(--pixso-lh-48);
  }

  .automotive-product__specs {
    padding: var(--product-detail-mobile-intro-padding-bottom) 0;
  }

  .automotive-product--industry-detail .automotive-product__specs {
    min-height: 0;
    padding: var(--product-detail-mobile-intro-padding-bottom) 0;
  }

  .automotive-product--industry-detail .automotive-product__scenarios {
    padding: var(--product-detail-mobile-intro-padding-bottom) 0;
  }

  .automotive-product--industry-detail .automotive-product__scenarios h2 {
    font-size: var(--pixso-fs-48);
    line-height: var(--pixso-lh-48);
  }

  .automotive-product__spec-table {
    margin-top: var(--product-detail-mobile-spec-table-top);
  }

  .automotive-product__spec-table :deep(.spec-table__buttons) {
    margin-bottom: var(--product-detail-mobile-spec-tabs-bottom);
    gap: var(--pixso-gap-sm);
  }

  .automotive-product__spec-table :deep(.spec-table__button),
  .automotive-product__spec-table :deep(.spec-table__button:nth-child(5)) {
    min-width: min(100%, var(--product-detail-mobile-spec-button-min)) !important;
    height: var(--product-detail-mobile-button-height) !important;
    padding: 0 var(--product-detail-mobile-button-padding-x) !important;
    font-size: var(--pixso-fs-14) !important;
    line-height: var(--pixso-lh-14) !important;
  }

  .automotive-product__spec-table :deep(.table-line__container) {
    padding: var(--pixso-gap-md) 0;
  }

  .automotive-product__spec-table :deep(.table-line__title),
  .automotive-product__spec-table :deep(.table-line__content) {
    width: 100%;
  }

  .automotive-product__spec-table :deep(.table-line__title) {
    font-size: var(--pixso-fs-20);
    line-height: var(--pixso-lh-20);
  }

  .automotive-product__spec-table :deep(.table-line__item) {
    font-size: var(--pixso-fs-16);
    line-height: var(--pixso-lh-16);
  }

  .product-figure__visual {
    width: min(100%, calc(100vw - var(--pixso-page-padding) * 2));
    max-width: 564px;
    overflow: visible;
  }

  .product-figure__tag,
  .automotive-product--industry-detail .product-figure__tag {
    width: min(92px, 26%);
    min-height: 0;
    padding: var(--product-detail-mobile-tag-padding-y) var(--product-detail-mobile-tag-padding-x);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.24);
    --product-detail-tag-icon-max: 24px;
    --product-detail-tag-icon-ratio: 34%;
    --product-detail-tag-text-max: 80px;
  }

  .product-figure__tag-text {
    max-width: var(--product-detail-tag-text-max);
    margin-top: 2px;
  }

  .product-figure--description .product-figure__tag--0,
  .automotive-product--industry-detail .product-figure--description .product-figure__tag--1 {
    top: 14%;
    left: 3%;
    right: auto;
  }

  .product-figure--description .product-figure__tag--1,
  .automotive-product--industry-detail .product-figure--description .product-figure__tag--0 {
    top: 14%;
    right: 3%;
    left: auto;
  }

  .product-figure--description .product-figure__tag--2,
  .automotive-product--industry-detail .product-figure--description .product-figure__tag--2 {
    top: 31%;
    right: 3%;
    left: auto;
  }

  .product-figure--description .product-figure__tag--3,
  .automotive-product--industry-detail .product-figure--description .product-figure__tag--3 {
    top: 31%;
    left: 3%;
    right: auto;
  }

  .product-figure--feature .product-figure__tag--0,
  .automotive-product--industry-detail .product-figure--feature .product-figure__tag--0 {
    top: 14%;
    left: 3%;
  }

  .product-figure--feature .product-figure__tag--1,
  .automotive-product--industry-detail .product-figure--feature .product-figure__tag--1 {
    top: 31%;
    left: 3%;
  }

  .product-figure--feature .product-figure__tag--2,
  .automotive-product--industry-detail .product-figure--feature .product-figure__tag--2 {
    top: 31%;
    right: 3%;
  }

  .product-figure--feature .product-figure__tag--3,
  .automotive-product--industry-detail .product-figure--feature .product-figure__tag--3 {
    top: 48%;
    right: 3%;
  }

  .product-figure--feature .product-figure__tag--4 {
    top: 48%;
    left: 3%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .automotive-product__body-text {
    transition: none;
    transform: none;
  }

  .automotive-product__body-text :deep(.automotive-product__text-char) {
    opacity: 1;
    transition: none;
    filter: none;
  }
}

</style>
